/**
 * TaskCampaignsField Component
 *
 * Handles related campaigns section:
 * - Link campaigns button
 * - Campaign cards display
 * - Remove campaign functionality
 *
 * Extracted from TaskForm.js for maintainability.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Card, Col, Row, Form, Button } from 'antd';
import { PaperClipOutlined, CalendarOutlined } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import moment from 'moment';
import { formatCampaignName, hasSpecialCharacters } from '../../../../../core/utils/campaignNaming';
import '../../styles/index.scss';

const TaskCampaignsField = ({
  form,
  hasCampaigns,
  relatedCampaigns,
  setRelatedCampaigns,
  setRelatedCampaignModal,
}) => {
  if (!hasCampaigns) {
    return null;
  }

  return (
    <Col className="task-campaigns-field">
      <Card
        className="task-campaigns-field__card"
        title={<Text className="task-campaigns-field__card-title">CAMPAIGNS</Text>}
      >
        <Row gutter={32}>
          <Col span={24}>
            <Form.Item
              label={
                <div>
                  <div>Related Campaigns</div>
                  <div className="task-campaigns-field__label-hint">
                    Link this task to one or more campaigns to track work and discussions in context
                  </div>
                </div>
              }
              colon={false}
            >
              <div className="task-campaigns-field__container">
                <Button
                  onClick={() => setRelatedCampaignModal(true)}
                  type="dashed"
                  icon={<PaperClipOutlined />}
                  className="task-campaigns-field__link-btn"
                >
                  {relatedCampaigns && relatedCampaigns.length > 0
                    ? '+ Add More Campaigns'
                    : '+ Link to Campaign'}
                </Button>

                {relatedCampaigns && relatedCampaigns.length > 0 ? (
                  <div className="task-campaigns-field__grid">
                    {relatedCampaigns.map((campaign) => (
                      <div
                        key={campaign.id}
                        className="task-campaigns-field__campaign-card"
                      >
                        <div className="task-campaigns-field__campaign-header">
                          <div className="task-campaigns-field__campaign-info">
                            <a
                              href={`/campaigns/details/${campaign.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="task-campaigns-field__campaign-link"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {!hasSpecialCharacters(campaign.name)
                                ? formatCampaignName(campaign)
                                : campaign.name}
                            </a>
                            <div className="task-campaigns-field__campaign-dates">
                              <CalendarOutlined />
                              {moment(campaign.startDate).format('MMM DD, YYYY')} - {moment(campaign.endDate).format('MMM DD, YYYY')}
                            </div>
                          </div>
                          <Button
                            type="text"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              const updatedCampaigns = relatedCampaigns.filter(
                                (c) => c.id !== campaign.id
                              );
                              setRelatedCampaigns(updatedCampaigns);
                              form.setFieldsValue({ relatedCampaigns: updatedCampaigns });
                            }}
                            className="task-campaigns-field__remove-btn"
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="task-campaigns-field__empty">
                    <div className="task-campaigns-field__empty-title">No campaigns linked</div>
                    <div className="task-campaigns-field__empty-hint">Link this task to campaigns to enable threaded discussions</div>
                  </div>
                )}
              </div>
            </Form.Item>
          </Col>
        </Row>
      </Card>
    </Col>
  );
};

TaskCampaignsField.propTypes = {
  form: PropTypes.object.isRequired,
  hasCampaigns: PropTypes.bool.isRequired,
  relatedCampaigns: PropTypes.array,
  setRelatedCampaigns: PropTypes.func.isRequired,
  setRelatedCampaignModal: PropTypes.func.isRequired,
};

TaskCampaignsField.defaultProps = {
  relatedCampaigns: [],
};

export default TaskCampaignsField;
